import { buildVisibleMenu, subItemPath, visibleSubItems } from '@/authz/menu-visibility';
import type { IPermissionSnapshot } from '@/authz/types';
import { getUserData } from '@/constants/global';
import type { IMenuItem } from '@/layout/utils';

export interface IPageEntry {
	title: string;
	/** Parent title for a sub-item, e.g. "System Management". */
	trail: string | null;
	path: string;
	section: string;
}

/**
 * Every page the user can open, flattened. Built from `buildVisibleMenu` — the SAME predicate the
 * sidebar uses — so the palette can never offer a destination the sidebar hides (the exact failure
 * `menu-visibility.ts` was centralised to prevent).
 */
export const visiblePages = (menu: readonly IMenuItem[], snapshot: IPermissionSnapshot): IPageEntry[] => {
	const out: IPageEntry[] = [];
	const seen = new Set<string>();
	const push = (entry: IPageEntry) => {
		if (seen.has(entry.path)) return;
		seen.add(entry.path);
		out.push(entry);
	};

	buildVisibleMenu(menu, snapshot).forEach(({ section, items }) =>
		items.forEach((item) => {
			const subs = visibleSubItems(item, snapshot);
			// A parent whose subs are the real pages is a grouper; list its subs, not the grouper.
			if (subs.length === 0) push({ title: item.title, trail: null, path: item.pageUrl, section });
			subs.forEach((sub) => push({ title: sub.title, trail: item.title, path: subItemPath(item, sub), section }));
		})
	);

	return out;
};

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9× ]+/g, ' ');

/**
 * Client-side page match. Every query word must appear in the title or its parent; ranking prefers a
 * title that starts with the query, then a word in the title that does.
 */
export const matchPages = (q: string, pages: readonly IPageEntry[], limit = 6): IPageEntry[] => {
	const query = norm(q).trim();
	if (!query) return [];
	const words = query.split(/\s+/).filter(Boolean);

	return pages
		.map((page) => {
			const title = norm(page.title);
			const hay = `${title} ${norm(page.trail ?? '')} ${norm(page.section)}`;
			if (!words.every((w) => hay.includes(w))) return null;
			let score = 1;
			if (title.startsWith(query)) score = 4;
			else if (title.split(/\s+/).some((w) => w.startsWith(words[0]))) score = 3;
			else if (title.includes(words[0])) score = 2;
			return { page, score };
		})
		.filter((x): x is { page: IPageEntry; score: number } => x !== null)
		.sort((a, b) => b.score - a.score || a.page.title.localeCompare(b.page.title))
		.slice(0, limit)
		.map((x) => x.page);
};

/**
 * Is this keydown the palette shortcut? ⌘K / Ctrl-K with no other modifier.
 *
 * Ignored inside a textarea, a select or a contenteditable surface (Markdown editors, the result
 * sheet transcription) — those are where ⌘K is most likely to mean something else, and hijacking
 * it mid-sentence loses the reader's place. Single-line inputs are allowed: no field in the app
 * binds ⌘K, and "search from the search box" is the most natural place to reach for it.
 */
export const isPaletteShortcut = (e: KeyboardEvent): boolean => {
	// Chrome's autofill fires keydown with NO `key`; IME composition and auto-repeat are not a press.
	if (typeof e.key !== 'string' || e.key.toLowerCase() !== 'k') return false;
	if (e.repeat || e.isComposing || e.defaultPrevented) return false;
	if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return false;
	const el = e.target as HTMLElement | null;
	if (!el) return true;
	if (el.closest?.('[data-palette-input]')) return true;
	return !(el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || Boolean(el.isContentEditable));
};

// ── Recent searches (per user, per browser, best effort) ──────

const RECENT_PREFIX = 'ielect.help.recentSearches.v1';
const RECENT_MAX = 5;

/**
 * Scoped by the signed-in user's id, so a shared machine never shows one person's searches to the
 * next. (`onLogout` also clears localStorage wholesale; this covers a user switch without one.)
 */
export const recentKey = (userId: string | number | null | undefined) => `${RECENT_PREFIX}:${userId ?? 'anonymous'}`;

const currentKey = () => recentKey(getUserData().user?.UserId);

/** Never throws: private mode or blocked storage reads as "no history". */
export const readRecent = (): string[] => {
	try {
		const raw = window.localStorage.getItem(currentKey());
		const parsed: unknown = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string').slice(0, RECENT_MAX) : [];
	} catch {
		return [];
	}
};

export const pushRecent = (q: string): string[] => {
	const query = q.trim();
	if (query.length < 2) return readRecent();
	const next = [query, ...readRecent().filter((s) => s.toLowerCase() !== query.toLowerCase())].slice(0, RECENT_MAX);
	try {
		window.localStorage.setItem(currentKey(), JSON.stringify(next));
	} catch {
		// Storage unavailable — the palette still works, it just forgets.
	}
	return next;
};

export const clearRecent = () => {
	try {
		window.localStorage.removeItem(currentKey());
	} catch {
		// ignore
	}
};
